/**
 * Wraps an async function so that concurrent calls share the same in-flight Promise.
 * Once the promise settles (resolves or rejects), the next call will start a fresh one.
 *
 * @param asyncFn - The original async function to de-duplicate
 * @returns A new function with the same signature as asyncFn, but de-duplicating in-flight calls
 */
export function singleFlight<T, A extends any[]>(asyncFn: (...args: A) => Promise<T>): (...args: A) => Promise<T> {
  let inFlight: Promise<T> | null = null;

  return (...args: A): Promise<T> => {
    if (!inFlight) {
      // Kick off the original asyncFn and capture its promise
      inFlight = asyncFn(...args).finally(() => {
        // Reset when done so future calls start a new request
        inFlight = null;
      });
    }
    return inFlight;
  };
}
